/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import React, { useEffect } from 'react';
import { List, ListItem, Typography } from '@mui/material';

import { PadLineItem } from '../../../components/Pad/Pad';
import TestIds from '../../../testIds';
import { StockAvailability } from '../../products/components/StockAvailability/StockAvailability';
import {
  StoreBox,
  StoreBoxRow,
  StoreContainer,
} from '../../products/components/StockAvailability/StoreSelector';
import { colors } from '@farmlands/mui-theme';
import { SeedStoreInfo } from '../types/SeedOrderFormType';
import { useAppDispatch } from '../../../store/rootStore';
import { getSeedStores, selectStores as selectSeedStores } from '../storage/seedStores.slice';
import { useSelector } from 'react-redux';

interface Props {
  productId?: number;
  value: SeedStoreInfo | undefined;
  setValue: React.Dispatch<React.SetStateAction<SeedStoreInfo | undefined>>;
}

export const SeedStoreSelector = ({ productId, value, setValue }: Props) => {
  const stores: SeedStoreInfo[] = useSelector(selectSeedStores);

  const dispatch = useAppDispatch();

  useEffect(() => {
    if (stores.length === 0) dispatch(getSeedStores());
  }, [stores]);

  const handleOnClick = (
    event: React.MouseEvent<HTMLLIElement, MouseEvent>,
    value: SeedStoreInfo,
  ) => {
    if (value && typeof value === 'object' && value.name && value.id) {
      setValue(stores.find((x) => x.id === value.id));
    } else {
      setValue(undefined);
    }
  };

  const renderSelectedPickUpStore = React.useMemo(() => {
    return (
      <StoreBox padding={'16px'} borderRadius={'8px'}>
        <StoreBoxRow
          sx={{
            borderLeft: `5px solid ${colors.primary500}`,
            padding: '8px 16px',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            columnGap: 2,
            rowGap: 1,
          }}
        >
          <StoreBox>
            <StoreBoxRow>
              <Typography variant='body1' color={'primary500'} fontWeight={700}>
                Pick up from:
              </Typography>
              <Typography
                variant='body1'
                color={'primary500'}
                data-testid={TestIds.feat.pickUpStore.selectedStoreBranchName}
              >
                {value?.name}
              </Typography>
            </StoreBoxRow>
            <StoreBox>
              <Typography
                variant='body2'
                data-testid={TestIds.feat.pickUpStore.selectedStoreAddress}
              >
                {value?.address}
              </Typography>
            </StoreBox>
          </StoreBox>
          <StockAvailability productId={productId ?? 0} storeId={value?.id} />
        </StoreBoxRow>
      </StoreBox>
    );
  }, [value?.name, value?.address]);

  return (
    <StoreContainer>
      {value?.name && renderSelectedPickUpStore}
      <List sx={{ p: 0, gap: 0 }}>
        {stores.map((store) => {
          return (
            <ListItem key={store.id} sx={{ m: 0, p: 0 }}>
              <PadLineItem
                sx={{ justifyContent: 'space-between' }}
                data-testid={TestIds.feat.pickUpStore.storeListItem}
                key={store.id}
                option={{
                  title: store.name,
                  subtitle: store.address,
                  sideElement: <StockAvailability productId={productId ?? 0} storeId={store.id} />,
                }}
                onClick={(event) => handleOnClick(event, store)}
              />
            </ListItem>
          );
        })}
      </List>
    </StoreContainer>
  );
};
